/**
 * Sorting Utilities
 * 
 * Pure functions for sorting projects by various criteria.
 * Isolated for easy testing and reuse.
 */

import { ProjectWithRelations } from "@shared/schema";

export type SortColumn = 'id' | 'project' | 'lead' | 'status' | 'createdAt' | null;
export type SortDirection = 'asc' | 'desc';

export interface SortOptions {
  column: SortColumn;
  direction: SortDirection;
}

/**
 * Sort projects based on the specified column and direction
 */
export function sortProjects(
  projects: ProjectWithRelations[],
  options: SortOptions
): ProjectWithRelations[] {
  if (!options.column) return projects;

  return [...projects].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (options.column) {
      case 'id':
        aValue = a.id;
        bValue = b.id;
        break;
      case 'project':
        aValue = a.projectName?.toLowerCase() || '';
        bValue = b.projectName?.toLowerCase() || '';
        break;
      case 'lead':
        aValue = a.leadArtist || '';
        bValue = b.leadArtist || '';
        break;
      case 'status':
        aValue = a.projectStatus || '';
        bValue = b.projectStatus || '';
        break;
      case 'createdAt':
        aValue = a.dateAdded ? new Date(a.dateAdded).getTime() : 0;
        bValue = b.dateAdded ? new Date(b.dateAdded).getTime() : 0;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return options.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return options.direction === 'asc' ? 1 : -1;
    return 0;
  });
}

/**
 * Toggle sort direction for a column
 * Returns new sort options based on the toggle logic:
 * - First click: sort ascending
 * - Second click: sort descending  
 * - Third click: clear sort
 */
export function toggleSortColumn(
  currentColumn: SortColumn,
  currentDirection: SortDirection,
  clickedColumn: Exclude<SortColumn, null>
): SortOptions {
  if (currentColumn === clickedColumn) {
    if (currentDirection === 'asc') {
      return { column: clickedColumn, direction: 'desc' };
    } else if (currentDirection === 'desc') {
      return { column: null, direction: 'asc' };
    }
  }
  
  return { column: clickedColumn, direction: 'asc' };
}
